'use client';

import { c as _c } from "react/compiler-runtime";
import { AccessibilitySolid, ArrowUpRight, BedSolid, ClockSolid, QuestionMark, SecuritySolid, ThumbsUpSolid, TrainSolid } from '@byway/icons';
import type { IconProps } from '@byway/icons/src/icons/Icon.types';
import { NumberFlowGroup } from '@number-flow/react';
import clsx from 'clsx';
import { useTranslations } from 'next-intl';
import type { ComponentType, ReactNode } from 'react';
import { Heading } from 'ui';
import { AnimatedPrice } from '~/components/AnimatedPrice';
import { Button } from '~/components/Button';
import { Divider } from '~/components/Divider';
import priceDetailsStyles from '~/components/Journey/PriceBar/PriceDetails.module.css';
import { Link } from '~/components/Link';
import { PERCENT_DEPOSIT } from '~/types/Payment.types';
import { TripLoyaltyBenefitsSection } from './TripLoyaltyBenefitsSection';
import loyaltyStyles from './TripLoyaltyPriceRow.module.css';
type IconLabelItem = {
  icon: ComponentType<IconProps>;
  label: string;
};
type TripPriceDetailsProps = {
  paxCount: number;
  roomCount: number;
  offerDeposit?: boolean;
  animate?: boolean;
  labelItem?: IconLabelItem[];
  price: {
    total: {
      value: number;
      formatted: string;
    };
    perPerson: {
      value: number;
      formatted: string;
    };
  };
  depositFormatted?: string;
  loyaltyOriginalTotalFormatted?: string;
  onClose?: () => void;
};
const CircleWrapper = t0 => {
  const $ = _c(5);
  const {
    children,
    variant: t1
  } = t0;
  const variant = t1 === undefined ? "default" : t1;
  const t2 = variant === "contrast";
  let t3;
  if ($[0] !== t2) {
    t3 = clsx(priceDetailsStyles.iconContainer, {
      [priceDetailsStyles.contrast]: t2
    });
    $[0] = t2;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== children || $[3] !== t3) {
    t4 = <div className={t3}>{children}</div>;
    $[2] = children;
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  return t4;
};
export const TripPriceDetails = t0 => {
  const $ = _c(89);
  const {
    paxCount,
    roomCount,
    labelItem,
    onClose,
    price,
    offerDeposit: t1,
    animate: t2,
    depositFormatted,
    loyaltyOriginalTotalFormatted
  } = t0;
  const offerDeposit = t1 === undefined ? false : t1;
  const animate = t2 === undefined ? false : t2;
  const t = useTranslations("journey");
  const tCommon = useTranslations("common");
  let t3;
  if ($[0] !== t) {
    t3 = t("includes_transport");
    $[0] = t;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== t3) {
    t4 = {
      icon: TrainSolid,
      label: t3
    };
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== t) {
    t5 = t("includes_accommodation");
    $[4] = t;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== t5) {
    t6 = {
      icon: BedSolid,
      label: t5
    };
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== t) {
    t7 = t("includes_package_protection");
    $[8] = t;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== t7) {
    t8 = {
      icon: SecuritySolid,
      label: t7
    };
    $[10] = t7;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== t) {
    t9 = t("includes_disruption_guarantee");
    $[12] = t;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== t9) {
    t10 = {
      icon: ThumbsUpSolid,
      label: t9
    };
    $[14] = t9;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== t) {
    t11 = t("includes_support");
    $[16] = t;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] !== t11) {
    t12 = {
      icon: ClockSolid,
      label: t11
    };
    $[18] = t11;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  let t13;
  if ($[20] !== t) {
    t13 = t("includes_accessibility");
    $[20] = t;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] !== t13) {
    t14 = {
      icon: AccessibilitySolid,
      label: t13
    };
    $[22] = t13;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] !== t10 || $[25] !== t12 || $[26] !== t14 || $[27] !== t4 || $[28] !== t6 || $[29] !== t8) {
    t15 = [t4, t6, t8, t10, t12, t14];
    $[24] = t10;
    $[25] = t12;
    $[26] = t14;
    $[27] = t4;
    $[28] = t6;
    $[29] = t8;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  const includedItems = t15;
  let t16;
  if ($[31] !== price.total.value) {
    t16 = Math.round(price.total.value * (PERCENT_DEPOSIT / 100));
    $[31] = price.total.value;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  const depositAmount = t16;
  const totalPriceFormatted = price.total.formatted;
  const showPriceExplanation = process.env.NEXT_PUBLIC_PRICE_EXPLANATION_ENABLED === "true";
  const paxPerRoom = Math.ceil(paxCount / Math.max(roomCount, 1));
  let t17;
  if ($[33] !== animate || $[34] !== price.total.value || $[35] !== totalPriceFormatted) {
    t17 = animate ? <AnimatedPrice amountGBP={price.total.value} /> : totalPriceFormatted;
    $[33] = animate;
    $[34] = price.total.value;
    $[35] = totalPriceFormatted;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  const totalPriceDisplay = t17;
  let t18;
  if ($[37] !== animate || $[38] !== depositAmount || $[39] !== depositFormatted || $[40] !== offerDeposit) {
    t18 = offerDeposit && (animate ? <AnimatedPrice amountGBP={depositAmount} /> : depositFormatted);
    $[37] = animate;
    $[38] = depositAmount;
    $[39] = depositFormatted;
    $[40] = offerDeposit;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  const depositDisplay = t18;
  let t19;
  if ($[42] !== t) {
    t19 = t("includes_heading");
    $[42] = t;
    $[43] = t19;
  } else {
    t19 = $[43];
  }
  let t20;
  if ($[44] !== t19) {
    t20 = <Heading as="h2" variant="h6">{t19}</Heading>;
    $[44] = t19;
    $[45] = t20;
  } else {
    t20 = $[45];
  }
  let t21;
  if ($[46] !== includedItems) {
    t21 = <ul className={priceDetailsStyles.includedList}>{includedItems.map(_temp)}</ul>;
    $[46] = includedItems;
    $[47] = t21;
  } else {
    t21 = $[47];
  }
  let t22;
  if ($[48] !== t20 || $[49] !== t21) {
    t22 = <div className={priceDetailsStyles.includedSection}>{t20}{t21}</div>;
    $[48] = t20;
    $[49] = t21;
    $[50] = t22;
  } else {
    t22 = $[50];
  }
  let t23;
  if ($[51] !== labelItem || $[52] !== t) {
    t23 = labelItem && labelItem.length > 0 && <div className={priceDetailsStyles.tripDetailsSection}><Divider variant="horizontal" /><Heading as="h2" variant="h6">{t("price_details_trip_details_heading")}</Heading>{labelItem.map(_temp2)}</div>;
    $[51] = labelItem;
    $[52] = t;
    $[53] = t23;
  } else {
    t23 = $[53];
  }
  let t24;
  if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <div className={priceDetailsStyles.divider}><Divider variant="horizontal" /></div>;
    $[54] = t24;
  } else {
    t24 = $[54];
  }
  let t25;
  if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = clsx(priceDetailsStyles.pricingNote, showPriceExplanation && priceDetailsStyles.pricingNoteHasFromPrice);
    $[55] = t25;
  } else {
    t25 = $[55];
  }
  let t26;
  if ($[56] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <div className={priceDetailsStyles.questionMarkCircle}><QuestionMark decorative={true} size="large" strokeWidth={2} /></div>;
    $[56] = t26;
  } else {
    t26 = $[56];
  }
  let t27;
  if ($[57] !== paxCount || $[58] !== paxPerRoom || $[59] !== roomCount || $[60] !== t) {
    t27 = showPriceExplanation && <p className={priceDetailsStyles.pricingNoteText}>{paxPerRoom <= 1 ? t("price_details_pricing_note_solo") : t("price_details_pricing_note_sharing", {
        person_count: paxCount,
        room_count: roomCount
      })}</p>;
    $[57] = paxCount;
    $[58] = paxPerRoom;
    $[59] = roomCount;
    $[60] = t;
    $[61] = t27;
  } else {
    t27 = $[61];
  }
  let t28;
  if ($[62] !== t) {
    t28 = t("price_details_learn_more");
    $[62] = t;
    $[63] = t28;
  } else {
    t28 = $[63];
  }
  let t29;
  if ($[64] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = <ArrowUpRight decorative={true} strokeWidth={2} />;
    $[64] = t29;
  } else {
    t29 = $[64];
  }
  let t30;
  if ($[65] !== t28) {
    t30 = <Link hasUnderline={true} href="https://www.byway.travel/journal/byway-trip-prices" target="_blank"><span className={priceDetailsStyles.pricingLink}>{t28} {t29}</span></Link>;
    $[65] = t28;
    $[66] = t30;
  } else {
    t30 = $[66];
  }
  let t31;
  if ($[67] !== t27 || $[68] !== t30) {
    t31 = <div className={priceDetailsStyles.pricingNoteSection}><div className={t25}>{t26}<div className={priceDetailsStyles.pricingNoteContent}>{t27}{t30}</div></div></div>;
    $[67] = t27;
    $[68] = t30;
    $[69] = t31;
  } else {
    t31 = $[69];
  }
  let t32;
  if ($[70] !== loyaltyOriginalTotalFormatted) {
    t32 = loyaltyOriginalTotalFormatted ? <div className={priceDetailsStyles.loyaltyBenefitsSection}><div className={priceDetailsStyles.loyaltyBenefitsDivider}><Divider variant="horizontal" /></div><TripLoyaltyBenefitsSection /></div> : null;
    $[70] = loyaltyOriginalTotalFormatted;
    $[71] = t32;
  } else {
    t32 = $[71];
  }
  let t33;
  if ($[72] !== animate || $[73] !== depositDisplay || $[74] !== loyaltyOriginalTotalFormatted || $[75] !== t || $[76] !== totalPriceDisplay || $[77] !== totalPriceFormatted) {
    t33 = totalPriceFormatted && <div className={priceDetailsStyles.totalSection}><div className={priceDetailsStyles.totalDivider}><Divider variant="horizontal" /></div>{animate ? <NumberFlowGroup><div className={priceDetailsStyles.totalPrice}><Heading as="h2" variant="h6">{t("price_details_total")}</Heading><span className={priceDetailsStyles.totalPriceValue}>{loyaltyOriginalTotalFormatted ? <span className={loyaltyStyles.originalTotal}>{loyaltyOriginalTotalFormatted}</span> : null}{totalPriceDisplay}</span></div>{depositDisplay ? <p className={priceDetailsStyles.depositBadge}>{t("price_details_deposit_note", {
            percent: PERCENT_DEPOSIT
          })} <strong>{depositDisplay}</strong></p> : null}</NumberFlowGroup> : <><div className={priceDetailsStyles.totalPrice}><Heading as="h2" variant="h6">{t("price_details_total")}</Heading><span className={priceDetailsStyles.totalPriceValue}>{totalPriceDisplay}</span></div>{depositDisplay ? <p className={priceDetailsStyles.depositBadge}>{t("price_details_deposit_note", {
            percent: PERCENT_DEPOSIT
          })} <strong>{depositDisplay}</strong></p> : null}</>}</div>;
    $[72] = animate;
    $[73] = depositDisplay;
    $[74] = loyaltyOriginalTotalFormatted;
    $[75] = t;
    $[76] = totalPriceDisplay;
    $[77] = totalPriceFormatted;
    $[78] = t33;
  } else {
    t33 = $[78];
  }
  let t34;
  if ($[79] !== onClose || $[80] !== tCommon) {
    t34 = onClose ? <div className={priceDetailsStyles.footer}><Button size="fullWidth" isPillShape={true} onPress={onClose} dataTestId="price-details-okay">{tCommon("okay")}</Button></div> : null;
    $[79] = onClose;
    $[80] = tCommon;
    $[81] = t34;
  } else {
    t34 = $[81];
  }
  let t35;
  if ($[82] !== t22 || $[83] !== t23 || $[84] !== t31 || $[85] !== t32 || $[86] !== t33 || $[87] !== t34) {
    t35 = <div className={priceDetailsStyles.content}>{t22}{t23}{t24}{t31}{t32}{t33}{t34}</div>;
    $[82] = t22;
    $[83] = t23;
    $[84] = t31;
    $[85] = t32;
    $[86] = t33;
    $[87] = t34;
    $[88] = t35;
  } else {
    t35 = $[88];
  }
  return t35;
};
function _temp(t0) {
  const {
    icon: Icon,
    label
  } = t0;
  return <li key={label} className={priceDetailsStyles.includedItem}><CircleWrapper><Icon decorative={true} size="large" /></CircleWrapper><span>{label}</span></li>;
}
function _temp2(t0) {
  const {
    icon: Icon_0,
    label: label_0
  } = t0;
  return <div key={label_0} className={priceDetailsStyles.tripDetailItem}><CircleWrapper variant="contrast"><Icon_0 decorative={true} size="large" /></CircleWrapper><span>{label_0}</span></div>;
}